// @ts-expect-error no type declarations for @mapbox/togeojson
import toGeoJSON from '@mapbox/togeojson'

export interface ImportMetadata {
  name: string | undefined
  description: string | undefined
  time: string | undefined
  bounds:
    | {
        ne: [number, number]
        sw: [number, number]
      }
    | undefined
  trackCount: number
  waypointCount: number
  routeCount: number
  totalPoints: number
  totalDistanceKm: number
  elevationGain: number | undefined
  elevationLoss: number | undefined
  elevationMax: number | undefined
  elevationMin: number | undefined
  hasTrackTimes: boolean
  trackStartTime: string | undefined
  trackEndTime: string | undefined
  sourceFormat: ImportFormat
}

export type ImportFormat = 'gpx' | 'kml' | 'kmz' | 'geojson'

export interface ParsedImport {
  /** GPX-specific: features from <rte> elements (authored paths) */
  routes: GeoJSON.FeatureCollection
  /** GPX-specific: features from <trk> elements (recorded paths) */
  tracks: GeoJSON.FeatureCollection
  /** GPX-specific: features from <wpt> elements (named points) */
  waypoints: GeoJSON.FeatureCollection
  /** All features as a flat list (used by KML/GeoJSON, and for preview/metrics) */
  allFeatures: GeoJSON.FeatureCollection
  metadata: ImportMetadata
}

export function detectFormat(fileName: string, content?: string | ArrayBuffer): ImportFormat {
  const ext = fileName.split('.').pop()?.toLowerCase()
  if (ext === 'gpx') return 'gpx'
  if (ext === 'kml') return 'kml'
  if (ext === 'kmz') return 'kmz'
  if (ext === 'geojson' || ext === 'json') return 'geojson'

  if (typeof content === 'string') {
    const trimmed = content.trimStart()
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) return 'geojson'
    if (trimmed.includes('<gpx')) return 'gpx'
    if (trimmed.includes('<kml')) return 'kml'
  }

  return 'gpx'
}

export async function parseImportFile(
  data: string | ArrayBuffer,
  fileName: string
): Promise<ParsedImport> {
  const format = detectFormat(fileName, data)

  switch (format) {
    case 'gpx':
      return parseGpxData(typeof data === 'string' ? data : new TextDecoder().decode(data))
    case 'kml':
      return parseKmlData(typeof data === 'string' ? data : new TextDecoder().decode(data))
    case 'kmz':
      return parseKmzData(
        data instanceof ArrayBuffer ? data : new TextEncoder().encode(data).buffer
      )
    case 'geojson':
      return parseGeoJsonData(typeof data === 'string' ? data : new TextDecoder().decode(data))
  }
}

function parseXmlDoc(xmlString: string): Document {
  const parser = new DOMParser()
  const doc = parser.parseFromString(xmlString, 'application/xml')
  const parseError = doc.querySelector('parsererror')
  if (parseError) {
    throw new Error('Invalid XML: parsing failed')
  }
  return doc
}

function parseGpxData(gpxString: string): ParsedImport {
  const doc = parseXmlDoc(gpxString)

  const trackDoc = separateGpxElements(doc, 'trk')
  const routeDoc = separateGpxElements(doc, 'rte')
  const waypointDoc = separateGpxElements(doc, 'wpt')

  const trackGeojson = toGeoJSON.gpx(trackDoc) as GeoJSON.FeatureCollection
  const routeGeojson = toGeoJSON.gpx(routeDoc) as GeoJSON.FeatureCollection
  const waypointGeojson = toGeoJSON.gpx(waypointDoc) as GeoJSON.FeatureCollection

  const allFeatures: GeoJSON.FeatureCollection = {
    type: 'FeatureCollection',
    features: [...trackGeojson.features, ...routeGeojson.features, ...waypointGeojson.features],
  }

  const metadataEl = doc.querySelector('metadata')
  /* eslint-disable @typescript-eslint/prefer-nullish-coalescing -- empty string should be treated as absent */
  const name =
    metadataEl?.querySelector('name')?.textContent ||
    doc.querySelector('gpx > name')?.textContent ||
    undefined
  const description = metadataEl?.querySelector('desc')?.textContent || undefined
  const time = metadataEl?.querySelector('time')?.textContent || undefined
  /* eslint-enable @typescript-eslint/prefer-nullish-coalescing */

  const trackCount = doc.querySelectorAll('trk').length
  const waypointCount = doc.querySelectorAll('wpt').length
  const routeCount = doc.querySelectorAll('rte').length

  const { hasTrackTimes, trackStartTime, trackEndTime } = detectTrackTimes(trackGeojson)

  return {
    routes: routeGeojson,
    tracks: trackGeojson,
    waypoints: waypointGeojson,
    allFeatures,
    metadata: {
      ...computeMetrics(allFeatures),
      name,
      description,
      time,
      trackCount,
      waypointCount,
      routeCount,
      hasTrackTimes,
      trackStartTime,
      trackEndTime,
      sourceFormat: 'gpx',
    },
  }
}

function parseKmlData(kmlString: string): ParsedImport {
  const doc = parseXmlDoc(kmlString)
  const allFeatures = toGeoJSON.kml(doc) as GeoJSON.FeatureCollection

  const nameEl = doc.querySelector('Document > name') ?? doc.querySelector('kml > name')
  const descEl =
    doc.querySelector('Document > description') ?? doc.querySelector('kml > description')

  /* eslint-disable @typescript-eslint/prefer-nullish-coalescing -- empty string should be treated as absent */
  const name = nameEl?.textContent || undefined
  const description = descEl?.textContent || undefined
  /* eslint-enable @typescript-eslint/prefer-nullish-coalescing */

  const lineCount = allFeatures.features.filter(
    f => f.geometry.type === 'LineString' || f.geometry.type === 'MultiLineString'
  ).length
  const pointCount = allFeatures.features.filter(f => f.geometry.type === 'Point').length

  const empty: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] }

  return {
    routes: empty,
    tracks: empty,
    waypoints: empty,
    allFeatures,
    metadata: {
      ...computeMetrics(allFeatures),
      name,
      description,
      time: undefined,
      trackCount: lineCount,
      waypointCount: pointCount,
      routeCount: 0,
      hasTrackTimes: false,
      trackStartTime: undefined,
      trackEndTime: undefined,
      sourceFormat: 'kml',
    },
  }
}

async function parseKmzData(data: ArrayBuffer): Promise<ParsedImport> {
  const { default: JSZip } = await import('jszip')
  const zip = await JSZip.loadAsync(data)

  let kmlContent: string | null = null
  for (const [path, file] of Object.entries(zip.files)) {
    if (path.endsWith('.kml') && !file.dir) {
      kmlContent = await file.async('string')
      break
    }
  }

  if (!kmlContent) {
    throw new Error('KMZ archive does not contain a KML file')
  }

  const result = parseKmlData(kmlContent)
  result.metadata.sourceFormat = 'kmz'
  return result
}

function parseGeoJsonData(jsonString: string): ParsedImport {
  const parsed = JSON.parse(jsonString) as GeoJSON.GeoJSON

  let allFeatures: GeoJSON.FeatureCollection
  if (parsed.type === 'FeatureCollection') {
    allFeatures = parsed
  } else if (parsed.type === 'Feature') {
    allFeatures = { type: 'FeatureCollection', features: [parsed] }
  } else {
    allFeatures = {
      type: 'FeatureCollection',
      features: [{ type: 'Feature', properties: {}, geometry: parsed }],
    }
  }

  const lineCount = allFeatures.features.filter(
    f => f.geometry.type === 'LineString' || f.geometry.type === 'MultiLineString'
  ).length
  const pointCount = allFeatures.features.filter(f => f.geometry.type === 'Point').length

  const empty: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] }

  return {
    routes: empty,
    tracks: empty,
    waypoints: empty,
    allFeatures,
    metadata: {
      ...computeMetrics(allFeatures),
      name: undefined,
      description: undefined,
      time: undefined,
      trackCount: lineCount,
      waypointCount: pointCount,
      routeCount: 0,
      hasTrackTimes: false,
      trackStartTime: undefined,
      trackEndTime: undefined,
      sourceFormat: 'geojson',
    },
  }
}

// ===== Shared helpers =====

function detectTrackTimes(tracks: GeoJSON.FeatureCollection): {
  hasTrackTimes: boolean
  trackStartTime: string | undefined
  trackEndTime: string | undefined
} {
  let earliest: string | undefined
  let latest: string | undefined

  for (const f of tracks.features) {
    const times = f.properties?.coordTimes as string[] | string[][] | undefined
    if (!times || (Array.isArray(times) && times.length === 0)) continue

    const flat = Array.isArray(times[0]) ? (times as string[][]).flat() : (times as string[])
    if (flat.length === 0) continue

    const first = flat[0]
    const last = flat[flat.length - 1]
    if (!earliest || first < earliest) earliest = first
    if (!latest || last > latest) latest = last
  }

  return {
    hasTrackTimes: earliest !== undefined,
    trackStartTime: earliest,
    trackEndTime: latest,
  }
}

function separateGpxElements(doc: Document, tagName: string): Document {
  const clone = doc.cloneNode(true) as Document
  const gpxEl = clone.querySelector('gpx')
  if (!gpxEl) return clone

  for (const tag of ['trk', 'rte', 'wpt']) {
    if (tag !== tagName) {
      gpxEl.querySelectorAll(tag).forEach(el => el.remove())
    }
  }
  return clone
}

function computeMetrics(geojson: GeoJSON.FeatureCollection): {
  bounds: ImportMetadata['bounds']
  totalPoints: number
  totalDistanceKm: number
  elevationGain: number | undefined
  elevationLoss: number | undefined
  elevationMax: number | undefined
  elevationMin: number | undefined
} {
  let minLat = Infinity,
    maxLat = -Infinity,
    minLng = Infinity,
    maxLng = -Infinity
  let totalPoints = 0
  let totalDistanceKm = 0
  const elevations: number[] = []

  for (const feature of geojson.features) {
    const coords = extractAllCoords(feature.geometry)
    totalPoints += coords.length

    for (const coord of coords) {
      const [lng, lat, ele] = coord as [number, number, number | undefined]
      if (lng < minLng) minLng = lng
      if (lng > maxLng) maxLng = lng
      if (lat < minLat) minLat = lat
      if (lat > maxLat) maxLat = lat
      if (ele !== undefined && !isNaN(ele)) elevations.push(ele)
    }

    if (feature.geometry.type === 'LineString') {
      totalDistanceKm += lineDistance(feature.geometry.coordinates)
    } else if (feature.geometry.type === 'MultiLineString') {
      for (const line of feature.geometry.coordinates) {
        totalDistanceKm += lineDistance(line)
      }
    }
  }

  const bounds =
    minLat !== Infinity
      ? { ne: [maxLat, maxLng] as [number, number], sw: [minLat, minLng] as [number, number] }
      : undefined

  let elevationGain: number | undefined
  let elevationLoss: number | undefined
  let elevationMax: number | undefined
  let elevationMin: number | undefined

  if (elevations.length > 0) {
    elevationMax = Math.max(...elevations)
    elevationMin = Math.min(...elevations)
    let gain = 0,
      loss = 0
    for (let i = 1; i < elevations.length; i++) {
      const diff = elevations[i] - elevations[i - 1]
      if (diff > 0) gain += diff
      else loss += Math.abs(diff)
    }
    elevationGain = Math.round(gain)
    elevationLoss = Math.round(loss)
  }

  return {
    bounds,
    totalPoints,
    totalDistanceKm: Math.round(totalDistanceKm * 100) / 100,
    elevationGain,
    elevationLoss,
    elevationMax,
    elevationMin,
  }
}

function extractAllCoords(geometry: GeoJSON.Geometry): number[][] {
  switch (geometry.type) {
    case 'Point':
      return [geometry.coordinates]
    case 'LineString':
      return geometry.coordinates
    case 'MultiLineString':
      return geometry.coordinates.flat()
    case 'Polygon':
      return geometry.coordinates.flat()
    case 'MultiPoint':
      return geometry.coordinates
    case 'MultiPolygon':
      return geometry.coordinates.flat(2)
    case 'GeometryCollection':
      return geometry.geometries.flatMap(g => extractAllCoords(g))
    default:
      return []
  }
}

function lineDistance(coords: number[][]): number {
  let total = 0
  for (let i = 1; i < coords.length; i++) {
    total += haversine(coords[i - 1][1], coords[i - 1][0], coords[i][1], coords[i][0])
  }
  return total
}

function haversine(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371
  const dLat = ((lat2 - lat1) * Math.PI) / 180
  const dLng = ((lng2 - lng1) * Math.PI) / 180
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}
